import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

interface OpportunityFormProps {
  onSuccess?: () => void;
}

export const OpportunityForm = ({ onSuccess }: OpportunityFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const title = formData.get('title') as string;
    const description = formData.get('description') as string;
    const location = formData.get('location') as string;
    const deadline = formData.get('deadline') as string;
    const requirements = formData.get('requirements') as string;
    const compensation = formData.get('compensation') as string;
    const remoteAllowed = formData.get('remote_allowed') === 'on';

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('opportunities')
        .insert({
          title,
          description,
          type: 'audition',
          location,
          remote_allowed: remoteAllowed,
          compensation,
          deadline: deadline ? new Date(deadline).toISOString() : null,
          requirements,
          created_by: user.id,
          status: 'published'
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Opportunity posted successfully",
      });
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(0);
      }
    } catch (error) {
      console.error('Error posting opportunity:', error);
      toast({
        title: "Error",
        description: "Failed to post opportunity. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input 
        name="title" 
        placeholder={t('opportunities.form.titlePlaceholder')} 
        required 
      />
      <Textarea 
        name="description" 
        placeholder={t('opportunities.form.descriptionPlaceholder')} 
        required 
      />
      <Input 
        name="location" 
        placeholder={t('opportunities.form.locationPlaceholder')} 
      />
      <Input 
        name="deadline" 
        type="datetime-local" 
      />
      <Textarea 
        name="requirements" 
        placeholder={t('opportunities.form.requirementsPlaceholder')} 
      />
      <Input 
        name="compensation" 
        placeholder={t('opportunities.form.compensationPlaceholder')} 
      />
      <div className="flex items-center gap-2">
        <input type="checkbox" id="remote_allowed" name="remote_allowed" />
        <label htmlFor="remote_allowed">
          {t('opportunities.form.remoteAllowed')}
        </label>
      </div>
      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('opportunities.form.submitting') : t('opportunities.form.submit')}
      </Button>
    </form>
  );
};